import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, debtByCustomer, type Customer } from '../lib/db'
import { bare } from '../lib/menuImages'
import { money } from '../lib/money'
import { Sheet } from './Sheet'

/**
 * Chọn khách quen để ghi sổ.
 *
 * Hiện số dư ngay cạnh mỗi tên, vì đó là thứ quyết định có nên cho nợ tiếp hay không —
 * bắt nhân viên bấm vào từng người mới biết thì họ sẽ không bấm, và quán cho nợ mù.
 *
 * Người nợ nhiều nhất đứng trên cùng. Nghe hơi ngược với "ai hay mua thì để trên", nhưng
 * ở màn hình này thì người nợ nhiều mới là người cần nhìn kỹ trước khi ghi thêm.
 */
export function CustomerPicker({
  onClose,
  onPick,
}: {
  onClose: () => void
  onPick: (customer: Customer) => void
}) {
  const [query, setQuery] = useState('')

  const customers = useLiveQuery(() => db.customers.orderBy('name').toArray(), [], [])
  const debts = useLiveQuery(() => debtByCustomer(), [], new Map<string, number>())

  const shown = useMemo(() => {
    const q = bare(query)
    const list = customers.filter(
      (c) => !q || bare(c.name).includes(q) || (c.phone ?? '').includes(q),
    )
    return list.sort((a, b) => (debts.get(b.id) ?? 0) - (debts.get(a.id) ?? 0))
  }, [customers, debts, query])

  return (
    <Sheet title="Ghi sổ cho ai" onClose={onClose}>
      {customers.length === 0 ? (
        <p className="sheet-note">
          Quán chưa có khách quen nào. Vào Cài đặt rồi Khách quen để thêm — chỉ chủ quán thêm
          được.
        </p>
      ) : (
        <>
          <label className="field">
            <span className="sr-only">Tìm khách</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Tìm tên hoặc số điện thoại…"
              autoFocus
            />
          </label>

          <div className="list">
            {shown.map((c) => {
              const debt = debts.get(c.id) ?? 0
              const over = c.debtLimit > 0 && debt >= c.debtLimit

              return (
                <button key={c.id} type="button" className="row-btn" onClick={() => onPick(c)}>
                  <span className="row-text">
                    <b>{c.name}</b>
                    <small>
                      {debt > 0 ? `Đang nợ ${money(debt)}đ` : 'Không nợ'}
                      {c.debtLimit > 0 ? ` · trần ${money(c.debtLimit)}đ` : ''}
                    </small>
                  </span>
                  {over && <span className="tag-over">Vượt trần</span>}
                  <span aria-hidden="true">›</span>
                </button>
              )
            })}
          </div>

          {shown.length === 0 && <p className="note">Không có khách nào tên như vậy.</p>}
        </>
      )}
    </Sheet>
  )
}
